import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, getToken, formatApiErrorDetail, SERVICE_STATUS_LABELS, SERVICE_STATUS_ORDER, SERVICE_STATUS_COLORS, DRIVER_STATUSES } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MarkPaidDialog } from "@/components/app/MarkPaidDialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  FileDown,
  Loader2,
  Trash2,
  CalendarClock,
  MapPin,
  Plane,
  Users as UsersIcon,
  Phone,
  Navigation,
  AlertTriangle,
  Pencil,
  Car,
  Mail,
  Camera,
  ImagePlus,
  Send,
  X,
} from "lucide-react";
import { toast } from "sonner";
import StepsMap from "@/components/app/StepsMap";

function getBrowserLocation(timeoutMs = 6000) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
      () => resolve(null),
      { timeout: timeoutMs, enableHighAccuracy: true, maximumAge: 30000 }
    );
  });
}

export default function ServiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [statusPending, setStatusPending] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [requestingEvidence, setRequestingEvidence] = useState(false);
  const isDriver = user?.role === "driver";
  const isStaff = user?.role === "admin" || user?.role === "user";
  const MAX_EVIDENCE = 6;

  const load = async () => {
    try {
      const { data } = await api.get(`/services/${id}`);
      setS(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    load();
    if (!isDriver) api.get("/drivers").then((r) => setDrivers(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  const changeStatus = async (status) => {
    setStatusPending(status);
    try {
      const location = isDriver ? await getBrowserLocation() : null;
      const { data } = await api.patch(`/services/${id}/status`, { status, location });
      setS(data);
      toast.success(`Estado: ${SERVICE_STATUS_LABELS[status]}`);
      if (status === "no_show") {
        setTimeout(() => {
          document
            .getElementById("no-show-evidence-panel")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 200);
        toast.info("Sube las pruebas fotográficas del no show (máx. 6)");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setStatusPending(null);
    }
  };

  const assignDriver = async (driverId) => {
    try {
      const { data } = await api.patch(`/services/${id}/assign`, {
        driver_id: driverId === "none" ? null : driverId,
      });
      setS(data);
      toast.success("Conductor actualizado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await api.post(`/services/${id}/notes`, { text: note });
      setNote("");
      await load();
    } finally {
      setSavingNote(false);
    }
  };

  const deleteSvc = async () => {
    if (!window.confirm("¿Eliminar esta reserva?")) return;
    await api.delete(`/services/${id}`);
    toast.success("Reserva eliminada");
    navigate("/services");
  };

  const openEdit = () => {
    setEditForm({
      booking_number: s.booking_number || "",
      client_name: s.client_name || "",
      client_phone: s.client_phone || "",
      client_email: s.client_email || "",
      pickup_date: s.pickup_date || "",
      pickup_time: s.pickup_time || "",
      pickup_location: s.pickup_location || "",
      dropoff_location: s.dropoff_location || "",
      passengers: s.passengers || 1,
      price: s.price != null ? s.price : "",
      flight_number: s.flight_number || "",
      vehicle_type: s.vehicle_type || "",
      notes: s.notes || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      const body = {
        ...editForm,
        passengers: parseInt(editForm.passengers) || 1,
        price: editForm.price === "" || editForm.price == null ? null : parseFloat(editForm.price),
      };
      const { data } = await api.patch(`/services/${id}`, body);
      setS(data);
      setEditOpen(false);
      toast.success("Reserva actualizada");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const uploadEvidence = async (files) => {
    if (!files || !files.length) return;
    const already = (s?.no_show_evidence || []).length;
    const remaining = MAX_EVIDENCE - already;
    if (remaining <= 0) {
      toast.error(`Máximo ${MAX_EVIDENCE} imágenes`);
      return;
    }
    const fd = new FormData();
    Array.from(files).slice(0, remaining).forEach((f) => fd.append("files", f));
    setUploadingEvidence(true);
    try {
      const { data } = await api.post(`/services/${id}/no-show-evidence`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setS(data);
      toast.success("Pruebas subidas");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setUploadingEvidence(false);
    }
  };

  const deleteEvidence = async (photoId) => {
    if (!window.confirm("¿Eliminar esta prueba?")) return;
    try {
      await api.delete(`/services/${id}/no-show-evidence/${photoId}`);
      await load();
      toast.success("Prueba eliminada");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    }
  };

  const requestEvidence = async () => {
    if (!window.confirm("¿Enviar solicitud de pruebas al conductor?")) return;
    setRequestingEvidence(true);
    try {
      const { data } = await api.post(`/services/${id}/request-evidence`);
      setS(data);
      toast.success("Solicitud enviada al conductor");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setRequestingEvidence(false);
    }
  };

  if (!s) return <div className="p-12 text-center text-zinc-500">Cargando…</div>;

  // Driver sequential order: from current status, only the listed next ones are allowed.
  const DRIVER_NEXT = {
    pendiente_asignacion: [],
    asignado: ["en_camino", "no_show"],
    en_camino: ["a_bordo", "no_show"],
    a_bordo: ["finalizado", "no_show"],
    finalizado: [],
    no_show: [],
    cancelado: [],
  };
  const DRIVER_STEPS = ["en_camino", "a_bordo", "finalizado", "no_show"];
  const DRIVER_STEP_ORDER = { en_camino: 1, a_bordo: 2, finalizado: 3 };
  const driverNext = DRIVER_NEXT[s.status] || [];
  const allowedStatuses = isDriver ? DRIVER_STEPS : SERVICE_STATUS_ORDER;
  const color = SERVICE_STATUS_COLORS[s.status] || "#0A0A0A";
  const isClosedForDriver = isDriver && (s.status === "finalizado" || s.status === "no_show");

  const driverPhotoUrl = s.driver?.has_photo
    ? `${API}/users/${s.driver.id}/photo?auth=${encodeURIComponent(getToken())}`
    : null;

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-[1400px] mx-auto" data-testid="service-detail-page">
      <button
        onClick={() => navigate(-1)}
        className="text-xs uppercase tracking-widest text-zinc-500 hover:text-black flex items-center gap-2 mb-6"
      >
        <ArrowLeft size={14} /> Volver
      </button>

      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 md:mb-8 pb-6 border-b border-black">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Reserva</div>
          <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-tight font-mono break-all">
            {s.booking_number}
          </h1>
          <div className="mt-3 inline-flex items-center gap-2">
            <span
              className="status-pill"
              style={{ borderColor: color, color }}
              data-testid="service-current-status"
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
              {SERVICE_STATUS_LABELS[s.status]}
            </span>
          </div>
        </div>
        {!isDriver && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={openEdit}
              variant="outline"
              className="rounded-none border-black h-10 text-xs uppercase tracking-widest"
              data-testid="edit-service-btn"
            >
              <Pencil size={14} className="mr-2" /> Editar
            </Button>
            <Button
              onClick={() =>
                window.open(
                  `${API}/services/${id}/export.pdf?auth=${encodeURIComponent(getToken())}`,
                  "_blank"
                )
              }
              variant="outline"
              className="rounded-none border-black h-10 text-xs uppercase tracking-widest"
              data-testid="service-pdf-btn"
            >
              <FileDown size={14} className="mr-2" /> PDF confirmación
            </Button>
            {user?.role === "admin" && (
              <Button
                variant="outline"
                onClick={deleteSvc}
                className="rounded-none border-[#E63946] text-[#E63946] hover:bg-[#FFEBEB] h-10 text-xs uppercase tracking-widest"
                data-testid="delete-service-btn"
              >
                <Trash2 size={14} className="mr-2" /> Eliminar
              </Button>
            )}
          </div>
        )}
      </header>

      {/* Status action panel */}
      <section className="border border-black bg-white p-6 mb-8" data-testid="service-status-panel">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
          {isClosedForDriver
            ? "Servicio cerrado"
            : isDriver
            ? "Pulsa el paso que acabas de realizar"
            : "Estado del servicio"}
        </div>
        <div className="flex flex-wrap gap-2">
          {allowedStatuses.map((st) => {
            const isCurrent = st === s.status;
            const isClickable = isDriver ? driverNext.includes(st) : !isCurrent;
            const isCompleted =
              isDriver &&
              DRIVER_STEP_ORDER[st] &&
              DRIVER_STEP_ORDER[s.status] &&
              DRIVER_STEP_ORDER[st] < DRIVER_STEP_ORDER[s.status];
            return (
              <button
                key={st}
                onClick={() => changeStatus(st)}
                disabled={!isClickable || statusPending === st}
                data-testid={`status-btn-${st}`}
                className={`px-4 h-10 text-xs uppercase tracking-widest border font-semibold transition-colors ${
                  isCurrent
                    ? "bg-black text-white border-black cursor-default"
                    : isCompleted
                    ? "bg-zinc-100 text-zinc-500 border-zinc-300 cursor-not-allowed line-through"
                    : isClickable
                    ? "bg-white text-black border-black hover:bg-black hover:text-white"
                    : "bg-white text-zinc-400 border-zinc-300 cursor-not-allowed"
                }`}
              >
                {statusPending === st ? (
                  <Loader2 className="animate-spin inline" size={12} />
                ) : (
                  SERVICE_STATUS_LABELS[st]
                )}
              </button>
            );
          })}
        </div>
        {isClosedForDriver && (
          <p
            className="text-[11px] text-zinc-500 mt-3"
            data-testid="service-closed-banner"
          >
            Este servicio está cerrado. Si necesitas modificarlo contacta con el centro de control.
          </p>
        )}
        {isDriver && !isClosedForDriver && (
          <p className="text-[11px] text-zinc-500 mt-3 flex items-center gap-1">
            <Navigation size={11} /> Activa la ubicación para que quede registrada la posición
            de cada paso.
          </p>
        )}
      </section>

      {/* No-show evidence panel */}
      {((isStaff && s.driver_id) ||
        s.status === "no_show" ||
        s.evidence_requested_at ||
        (s.no_show_evidence || []).length > 0) && (
        <section
          id="no-show-evidence-panel"
          className="border-2 border-[#E63946] bg-red-50 p-6 mb-8"
          data-testid="no-show-evidence-panel"
        >
          <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#E63946] mb-1 flex items-center gap-2 font-bold">
                <AlertTriangle size={14} /> Pruebas del no show
              </div>
              <h2 className="font-display text-2xl font-bold tracking-tight">
                {isDriver
                  ? s.evidence_requested_at
                    ? "Se te han solicitado pruebas"
                    : "Sube las pruebas del no show"
                  : `Evidencias · ${(s.no_show_evidence || []).length}/${MAX_EVIDENCE}`}
              </h2>
              <p className="text-sm text-zinc-700 mt-1">
                {isDriver
                  ? `Sube hasta ${MAX_EVIDENCE} fotos como prueba de que el cliente no apareció (llegada al punto, entorno, hora, etc.).`
                  : s.evidence_requested_at
                  ? `Solicitud enviada al conductor · ${new Date(s.evidence_requested_at).toLocaleString("es-ES")}`
                  : "Puedes solicitar pruebas al conductor si es necesario."}
              </p>
            </div>
            {isStaff && s.driver_id && (s.no_show_evidence || []).length < MAX_EVIDENCE && (
              <Button
                onClick={requestEvidence}
                disabled={requestingEvidence}
                className="rounded-none btn-primary h-10 text-xs uppercase tracking-widest"
                data-testid="request-evidence-btn"
              >
                {requestingEvidence ? (
                  <Loader2 className="animate-spin mr-2" size={14} />
                ) : (
                  <Send size={14} className="mr-2" />
                )}
                {s.evidence_requested_at ? "Reenviar solicitud" : "Solicitar pruebas"}
              </Button>
            )}
          </div>

          {(s.no_show_evidence || []).length > 0 && (
            <div
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4"
              data-testid="no-show-evidence-grid"
            >
              {(s.no_show_evidence || []).map((ph) => (
                <div
                  key={ph.id}
                  className="relative border-2 border-black bg-white aspect-square overflow-hidden group"
                  data-testid={`evidence-photo-${ph.id}`}
                >
                  <a
                    href={`${API}/services/${id}/no-show-evidence/${ph.id}?auth=${encodeURIComponent(getToken())}`}
                    target="_blank"
                    rel="noreferrer"
                    className="block w-full h-full"
                  >
                    <img
                      src={`${API}/services/${id}/no-show-evidence/${ph.id}?auth=${encodeURIComponent(getToken())}`}
                      alt="Prueba"
                      className="w-full h-full object-cover"
                    />
                  </a>
                  {user?.role === "admin" && (
                    <button
                      onClick={() => deleteEvidence(ph.id)}
                      className="absolute top-1 right-1 w-6 h-6 bg-white border border-black hover:bg-[#E63946] hover:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-evidence-${ph.id}`}
                      aria-label="Eliminar"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {(s.no_show_evidence || []).length < MAX_EVIDENCE &&
            (isDriver || isStaff) && (
              <div>
                <label
                  htmlFor="evidence-upload-input"
                  className={`inline-flex items-center gap-2 px-4 h-11 border-2 border-black bg-white hover:bg-black hover:text-white cursor-pointer text-xs uppercase tracking-widest font-semibold ${
                    uploadingEvidence ? "opacity-60 pointer-events-none" : ""
                  }`}
                  data-testid="upload-evidence-label"
                >
                  {uploadingEvidence ? (
                    <Loader2 className="animate-spin" size={14} />
                  ) : (
                    <ImagePlus size={14} />
                  )}
                  Subir pruebas ({(s.no_show_evidence || []).length}/{MAX_EVIDENCE})
                </label>
                <input
                  id="evidence-upload-input"
                  data-testid="upload-evidence-input"
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => {
                    uploadEvidence(e.target.files);
                    if (e.target.files && e.target.files.length) {
                      toast.info(
                        "Recuerda: las fotos deben tener los datos de ubicación (GPS) activados. Si subes desde galería, verifica que la foto conserve la geolocalización."
                      );
                    }
                    e.target.value = "";
                  }}
                />
                {isDriver && (
                  <p className="text-[11px] text-zinc-600 mt-2 flex items-center gap-1">
                    <Camera size={11} /> Puedes tomar la foto o elegirla de la galería.
                    Importante: debe tener <b>GPS activado</b> para que se guarde la ubicación.
                  </p>
                )}
              </div>
            )}
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div className={`${isDriver ? "lg:col-span-5" : "lg:col-span-3"} space-y-8`}>
          <section className="border border-black bg-white">
            <div className="border-b border-black p-4 font-display font-semibold text-sm uppercase tracking-widest">
              Servicio
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <CalendarClock size={16} className="mt-1 text-zinc-500" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                    Recogida
                  </div>
                  <div className="font-semibold">
                    {s.pickup_date} · {s.pickup_time}
                  </div>
                </div>
              </div>
              {!isDriver && (
                <div className="flex items-start gap-3">
                  <UsersIcon size={16} className="mt-1 text-zinc-500" />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                      Pasajeros
                    </div>
                    <div className="font-semibold">{s.passengers || 1}</div>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3 sm:col-span-2">
                <MapPin size={16} className="mt-1 text-zinc-500" />
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Origen</div>
                  <div className="font-semibold">{s.pickup_location}</div>
                  {isDriver && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        s.pickup_location
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs underline underline-offset-4 mt-1"
                    >
                      <Navigation size={12} /> Abrir en mapas
                    </a>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-3 sm:col-span-2">
                <MapPin size={16} className="mt-1 text-zinc-500" />
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Destino</div>
                  <div className="font-semibold">{s.dropoff_location}</div>
                  {isDriver && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        s.dropoff_location
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs underline underline-offset-4 mt-1"
                    >
                      <Navigation size={12} /> Abrir en mapas
                    </a>
                  )}
                </div>
              </div>
              {s.flight_number && (
                <div className="flex items-start gap-3">
                  <Plane size={16} className="mt-1 text-zinc-500" />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Vuelo</div>
                    <div className="font-semibold">{s.flight_number}</div>
                    <a
                      href={`https://www.google.com/search?q=${encodeURIComponent("vuelo " + s.flight_number + " estado")}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs underline underline-offset-4 mt-1"
                      data-testid="flight-tracking-link"
                    >
                      <Navigation size={12} /> Ver seguimiento del vuelo
                    </a>
                  </div>
                </div>
              )}
              {s.vehicle_type && (
                <div className="flex items-start gap-3">
                  <Car size={16} className="mt-1 text-zinc-500" />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                      Vehículo solicitado
                    </div>
                    <div className="font-semibold">{s.vehicle_type}</div>
                  </div>
                </div>
              )}
              {!isDriver && s.price != null && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Precio</div>
                  <div className="font-display text-xl font-bold">
                    {Number(s.price).toFixed(2)} €
                  </div>
                  <div className="mt-1">
                    {s.payment_status === "paid" ? (
                      <span
                        className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-emerald-700 text-emerald-700 bg-emerald-50"
                        data-testid="payment-badge-paid"
                      >
                        Pagado{s.paid_at ? ` · ${new Date(s.paid_at).toLocaleDateString("es-ES")}` : ""}
                        {s.payment_method ? ` · ${s.payment_method}` : ""}
                      </span>
                    ) : (
                      <>
                        <span
                          className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-[#E63946] text-[#E63946] bg-red-50"
                          data-testid="payment-badge-unpaid"
                        >
                          Pendiente de pago
                        </span>
                        {user?.role === "admin" && <MarkPaidDialog serviceId={s.id} onDone={() => load()} />}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="border-t border-zinc-200 p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Cliente</div>
                <div className="font-display text-lg font-semibold">{s.client_name}</div>
                <a
                  href={`tel:${s.client_phone}`}
                  className="inline-flex items-center gap-2 text-sm underline underline-offset-4 mt-1"
                  data-testid="client-phone-link"
                >
                  <Phone size={14} /> {s.client_phone}
                </a>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(s.client_phone || "");
                      toast.success("Teléfono copiado");
                    }}
                    className="px-2 h-8 text-[10px] uppercase tracking-widest border border-black bg-white hover:bg-black hover:text-white transition-colors"
                    data-testid="copy-phone-btn"
                  >
                    Copiar
                  </button>
                  <a
                    href={`https://wa.me/${(s.client_phone || "").replace(/[^\d+]/g, "").replace(/^\+/, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="px-2 h-8 inline-flex items-center text-[10px] uppercase tracking-widest border border-black bg-white hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-colors"
                    data-testid="whatsapp-phone-btn"
                  >
                    WhatsApp
                  </a>
                  <a
                    href={`sms:${s.client_phone}`}
                    className="px-2 h-8 inline-flex items-center text-[10px] uppercase tracking-widest border border-black bg-white hover:bg-black hover:text-white transition-colors"
                    data-testid="sms-phone-btn"
                  >
                    SMS
                  </a>
                </div>
                {!isDriver && s.client_email && (
                  <a
                    href={`mailto:${s.client_email}`}
                    className="inline-flex items-center gap-2 text-sm underline underline-offset-4 mt-1 block"
                    data-testid="client-email-link"
                  >
                    <Mail size={14} /> {s.client_email}
                  </a>
                )}
              </div>
              {s.notes && (
                <div className="border-2 border-black p-4 bg-yellow-50" data-testid="service-notes-card">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                    Notas para el conductor
                  </div>
                  <p className="text-sm whitespace-pre-wrap font-medium">{s.notes}</p>
                </div>
              )}
              {s.rating && (
                <div className="border-2 border-black p-4 bg-white sm:col-span-2" data-testid="service-rating-card">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                    Calificación del cliente
                  </div>
                  <div className="flex items-center gap-1 mb-2">
                    {[1,2,3,4,5].map((n) => (
                      <span key={n} className={n <= s.rating.stars ? "text-black" : "text-zinc-300"} style={{fontSize:18}}>★</span>
                    ))}
                    <span className="ml-2 text-sm font-semibold">{s.rating.stars}/5</span>
                  </div>
                  {s.rating.comment && (
                    <p className="text-sm whitespace-pre-wrap text-zinc-700">"{s.rating.comment}"</p>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Seguimiento / Timeline — SOLO para staff, NO para conductor */}
          {!isDriver && (
            <section className="border border-black bg-white">
              <div className="border-b border-black p-4 font-display font-semibold text-sm uppercase tracking-widest">
                Seguimiento
              </div>
              <div className="p-5 space-y-6">
                <StepsMap steps={s.timeline} />

                <form onSubmit={addNote}>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Agrega una nota interna…"
                    className="rounded-none border-black min-h-[80px]"
                    data-testid="service-note-input"
                  />
                  <div className="flex justify-end mt-2">
                    <Button
                      type="submit"
                      disabled={savingNote || !note.trim()}
                      className="rounded-none btn-primary h-10 text-xs uppercase tracking-widest"
                      data-testid="add-service-note-btn"
                    >
                      {savingNote ? <Loader2 className="animate-spin" size={14} /> : "Agregar"}
                    </Button>
                  </div>
                </form>
                <ol className="relative border-l border-zinc-300 pl-6 space-y-5">
                  {[...(s.timeline || [])].reverse().map((t) => (
                    <li key={t.id} className="relative">
                      <span
                        className={`absolute -left-[27px] top-1 w-3 h-3 ${
                          t.anomaly
                            ? "bg-[#E63946]"
                            : t.type === "status" || t.type === "assignment"
                            ? "bg-black"
                            : "bg-white border border-black"
                        }`}
                      />
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                        {new Date(t.created_at).toLocaleString("es-ES")} · {t.author_name}
                      </div>
                      <div
                        className={`mt-1 text-sm whitespace-pre-wrap ${
                          t.type !== "note" ? "font-semibold" : ""
                        }`}
                      >
                        {t.text}
                      </div>
                      <div className="flex items-center gap-3 flex-wrap mt-1">
                        {t.location && (
                          <a
                            href={`https://www.google.com/maps?q=${t.location.lat},${t.location.lng}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs underline underline-offset-4"
                            data-testid={`timeline-location-${t.id}`}
                          >
                            <MapPin size={12} /> Ver ubicación
                            {t.location.accuracy
                              ? ` (±${Math.round(t.location.accuracy)} m)`
                              : ""}
                          </a>
                        )}
                        {t.distance_from_prev_km != null && (
                          <span
                            className={`text-[10px] uppercase tracking-widest font-semibold ${
                              t.anomaly ? "text-[#E63946]" : "text-zinc-500"
                            }`}
                          >
                            {t.anomaly && <AlertTriangle size={10} className="inline mr-1" />}
                            {t.distance_from_prev_km} km desde el paso anterior
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </section>
          )}
        </div>

        {!isDriver && (
          <div className="lg:col-span-2 space-y-8">
            <section className="border border-black bg-white">
              <div className="border-b border-black p-4 font-display font-semibold text-sm uppercase tracking-widest">
                Conductor
              </div>
              <div className="p-5">
                {s.driver ? (
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-20 h-20 border-2 border-black bg-zinc-100 flex items-center justify-center overflow-hidden">
                      {driverPhotoUrl ? (
                        <img
                          src={driverPhotoUrl}
                          alt={s.driver.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-2xl font-bold">
                          {(s.driver.name || "?").slice(0, 1)}
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="font-display text-lg font-bold">{s.driver.name}</div>
                      {s.driver.phone && (
                        <a
                          href={`tel:${s.driver.phone}`}
                          className="text-sm underline underline-offset-4 inline-flex items-center gap-2"
                        >
                          <Phone size={14} /> {s.driver.phone}
                        </a>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-zinc-500 mb-4">Sin conductor asignado</p>
                )}

                {isStaff && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                      Asignar / cambiar conductor
                    </div>
                    <Select value={s.driver_id || "none"} onValueChange={assignDriver}>
                      <SelectTrigger
                        className="rounded-none border-black h-10"
                        data-testid="reassign-driver-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="none">Sin asignar</SelectItem>
                        {drivers.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </section>
          </div>
        )}
      </div>

      {/* Edit reservation modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-none border-black max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase tracking-tight">
              Editar reserva
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveEdit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Nº reserva</Label>
              <Input
                value={editForm.booking_number || ""}
                onChange={(e) => setEditForm({ ...editForm, booking_number: e.target.value })}
                className="mt-2 h-11 rounded-none border-black"
                data-testid="edit-booking-number"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Nº vuelo</Label>
              <Input
                value={editForm.flight_number || ""}
                onChange={(e) => setEditForm({ ...editForm, flight_number: e.target.value })}
                className="mt-2 h-11 rounded-none border-black"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Cliente</Label>
              <Input
                value={editForm.client_name || ""}
                onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })}
                className="mt-2 h-11 rounded-none border-black"
                data-testid="edit-client-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Teléfono</Label>
              <Input
                value={editForm.client_phone || ""}
                onChange={(e) => setEditForm({ ...editForm, client_phone: e.target.value })}
                className="mt-2 h-11 rounded-none border-black"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Email cliente</Label>
              <Input
                type="email"
                value={editForm.client_email || ""}
                onChange={(e) => setEditForm({ ...editForm, client_email: e.target.value })}
                className="mt-2 h-11 rounded-none border-black"
                data-testid="edit-client-email"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Vehículo solicitado</Label>
              <Select
                value={editForm.vehicle_type || ""}
                onValueChange={(v) => setEditForm({ ...editForm, vehicle_type: v })}
              >
                <SelectTrigger
                  className="mt-2 h-11 rounded-none border-black"
                  data-testid="edit-vehicle-type"
                >
                  <SelectValue placeholder="Selecciona vehículo" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="mini vans">Mini Vans</SelectItem>
                  <SelectItem value="minibus">Minibus</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Fecha</Label>
              <Input
                type="date"
                value={editForm.pickup_date || ""}
                onChange={(e) => setEditForm({ ...editForm, pickup_date: e.target.value })}
                className="mt-2 h-11 rounded-none border-black"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Hora</Label>
              <Input
                type="time"
                value={editForm.pickup_time || ""}
                onChange={(e) => setEditForm({ ...editForm, pickup_time: e.target.value })}
                className="mt-2 h-11 rounded-none border-black"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest">Origen</Label>
              <Input
                value={editForm.pickup_location || ""}
                onChange={(e) => setEditForm({ ...editForm, pickup_location: e.target.value })}
                className="mt-2 h-11 rounded-none border-black"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest">Destino</Label>
              <Input
                value={editForm.dropoff_location || ""}
                onChange={(e) => setEditForm({ ...editForm, dropoff_location: e.target.value })}
                className="mt-2 h-11 rounded-none border-black"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Pasajeros</Label>
              <Input
                type="number"
                min={1}
                value={editForm.passengers || 1}
                onChange={(e) => setEditForm({ ...editForm, passengers: e.target.value })}
                className="mt-2 h-11 rounded-none border-black"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Precio (€)</Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={editForm.price ?? ""}
                onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                className="mt-2 h-11 rounded-none border-black"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest">Notas</Label>
              <Textarea
                value={editForm.notes || ""}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                className="mt-2 rounded-none border-black min-h-[100px]"
              />
            </div>
            <DialogFooter className="md:col-span-2 flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditOpen(false)}
                className="rounded-none border-black h-11 px-5 text-xs uppercase tracking-widest"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={savingEdit}
                className="rounded-none btn-primary h-11 px-6 text-xs uppercase tracking-widest"
                data-testid="save-edit-service-btn"
              >
                {savingEdit ? <Loader2 className="animate-spin" size={14} /> : "Guardar cambios"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
